import { PAGE_BG_STYLE } from "@/lib/pageBg";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { PageHero } from "@/components/PageHero";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Crown, Star, Sparkles, User, ExternalLink, Trophy, 
  Calendar, Wallet, Users, Zap, TrendingUp, 
  Share2, Heart, Award
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { BADGE_METADATA, BADGE_IMAGES, OfficialBadgeType } from "@/hooks/useBadges";

interface EarlyCreator {
  id: string;
  profile_id: string | null;
  wallet_address: string;
  claimed_at: string;
  display_name: string | null;
  avatar_url: string | null;
  nft_count: number;
  position: number;
}

// Badge info with how to earn instructions
const BADGE_INFO: Record<OfficialBadgeType, { 
  icon: React.ReactNode; 
  gradient: string;
  howToEarn: string;
  isExclusive?: boolean;
  linkText?: string;
  linkUrl?: string;
}> = {
  early_creator: {
    icon: <Crown className="w-6 h-6" />,
    gradient: "from-amber-400 to-orange-500",
    howToEarn: "Awarded automatically to the first 10 creators who join FLOW Marketplace. This exclusive badge is auto-assigned when you meet the criteria — no manual claim needed!",
    isExclusive: true
    // No external link - scroll handled internally
  },
  rising_creator: {
    icon: <TrendingUp className="w-6 h-6" />,
    gradient: "from-green-400 to-emerald-500",
    howToEarn: "Create 3 or more approved NFTs on the platform. Start minting your creations and get them approved to earn this badge!",
    linkText: "Create NFTs",
    linkUrl: "/marketplace"
  },
  power_minter: {
    icon: <Zap className="w-6 h-6" />,
    gradient: "from-purple-400 to-violet-500",
    howToEarn: "Create 10 or more NFTs on the platform. Keep minting and building your collection to become a Power Minter!",
    linkText: "Create NFTs",
    linkUrl: "/marketplace"
  },
  top_seller: {
    icon: <Trophy className="w-6 h-6" />,
    gradient: "from-yellow-400 to-amber-500",
    howToEarn: "Achieve the highest number of sales on the platform. This competitive badge goes to the #1 seller and is updated in real-time.",
    linkText: "View Leaderboard",
    linkUrl: "/leaderboard"
  },
  top_sharer: {
    icon: <Share2 className="w-6 h-6" />,
    gradient: "from-blue-400 to-cyan-500",
    howToEarn: "Become the most active sharer on the platform. Share NFTs on social media to help spread the word and earn this badge!",
    linkText: "View Leaderboard",
    linkUrl: "/leaderboard"
  },
  most_liked: {
    icon: <Heart className="w-6 h-6" />,
    gradient: "from-pink-400 to-rose-500",
    howToEarn: "Become the most liked creator on the platform. Create amazing content that the community loves to earn this badge!",
    linkText: "View Leaderboard",
    linkUrl: "/leaderboard"
  }
};

export default function EarlyCreators() {
  const [creators, setCreators] = useState<EarlyCreator[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Scroll to top on mount to show the title
    window.scrollTo(0, 0);
    fetchEarlyCreators();
  }, []);

  const fetchEarlyCreators = async () => {
    setLoading(true);
    try {
      const { data: claims } = await supabase
        .from("early_creator_claims")
        .select("id, wallet_address, claimed_at")
        .order("claimed_at", { ascending: true })
        .limit(10);

      if (!claims || claims.length === 0) {
        setCreators([]);
        setLoading(false);
        return;
      }

      const wallets = claims.map(c => c.wallet_address);
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, wallet_address, display_name, avatar_url")
        .in("wallet_address", wallets);

      const { data: nftCounts } = await supabase
        .from("nfts")
        .select("owner_wallet")
        .in("owner_wallet", wallets);

      const creatorData: EarlyCreator[] = claims.map((claim, index) => {
        const profile = profiles?.find(p => p.wallet_address === claim.wallet_address);
        const nftCount = nftCounts?.filter(n => n.owner_wallet === claim.wallet_address).length || 0;

        return {
          id: claim.id,
          profile_id: profile?.id || null,
          wallet_address: claim.wallet_address,
          claimed_at: claim.claimed_at,
          display_name: profile?.display_name || null,
          avatar_url: profile?.avatar_url || null,
          nft_count: nftCount,
          position: index + 1
        };
      });

      setCreators(creatorData);
    } catch (error) {
      console.error("Error fetching early creators:", error);
    }
    setLoading(false);
  };

  const scrollToEarlyCreators = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('early-creators')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen relative" style={PAGE_BG_STYLE}>
      <AppHeader currentPage="early-creators" />
      
      <main className="container max-w-7xl mx-auto px-4 pt-20 pb-24">
        <PageHero
          title="Badges & How to Earn"
          subtitle="Badges are automatically awarded when you achieve milestones on FLOW Marketplace. No manual claiming needed — just keep creating and engaging!"
          badge={
            <Badge className="bg-gradient-to-r from-primary to-accent text-primary-foreground border-0 px-4 py-1.5 text-sm font-bold">
              <Sparkles className="w-4 h-4 mr-2" />
              Achievement System
            </Badge>
          }
        />

        {/* All Badges Section */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
            <Award className="w-6 h-6 text-primary" />
            All Available Badges
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {(Object.keys(BADGE_INFO) as OfficialBadgeType[]).map((badgeType) => {
              const badge = BADGE_INFO[badgeType];
              const metadata = BADGE_METADATA[badgeType];
              const isEarlyCreator = badgeType === 'early_creator';

              return (
                <Card 
                  key={badgeType} 
                  className={`relative overflow-hidden border-2 ${badge.isExclusive ? 'border-amber-500/30' : 'border-border/50'} hover:border-primary/50 transition-all duration-300`}
                >
                  {badge.isExclusive && (
                    <div className="absolute top-3 right-3">
                      <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                        <Star className="w-3 h-3 mr-1" />
                        Exclusive
                      </Badge>
                    </div>
                  )}

                  <CardContent className="p-6">
                    <div className="flex gap-4">
                      {/* Badge Image/Icon */}
                      <div className="flex-shrink-0">
                        <div className={`w-20 h-20 rounded-xl bg-gradient-to-br ${badge.gradient} p-0.5`}>
                          <div className="w-full h-full rounded-xl bg-background/90 flex items-center justify-center overflow-hidden">
                            <img 
                              src={BADGE_IMAGES[badgeType]}
                              alt={metadata.name}
                              className="w-full h-full object-cover rounded-xl"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                e.currentTarget.nextElementSibling?.classList.remove('hidden');
                              }}
                            />
                            <div className={`hidden text-white bg-gradient-to-br ${badge.gradient} w-full h-full flex items-center justify-center rounded-xl`}>
                              {badge.icon}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Badge Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-foreground mb-1">
                          {metadata.name}
                        </h3>
                        <p className="text-sm text-muted-foreground mb-3">
                          {badge.howToEarn}
                        </p>

                        {/* Link or Early Creator Stats */}
                        {isEarlyCreator ? (
                          <button
                            onClick={scrollToEarlyCreators}
                            className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors font-semibold mt-2"
                          >
                            <Users className="w-4 h-4" />
                            <span>{creators.length}/10 Claimed</span>
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        ) : badge.linkUrl && (
                          <Link 
                            to={badge.linkUrl}
                            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                          >
                            {badge.linkText}
                            <ExternalLink className="w-3 h-3" />
                          </Link>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Early Creators Section */}
        <section id="early-creators" className="scroll-mt-20">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500">
              <Crown className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground">Early Creators Hall of Fame</h2>
              <p className="text-sm text-muted-foreground">The first 10 pioneers of FLOW Marketplace</p>
            </div>
          </div>

          {/* NFT Preview */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl blur-xl opacity-30 animate-pulse" />
              <img 
                src="/early-creator-nft.jpeg"
                alt="Early Creator NFT"
                className="relative w-32 h-32 rounded-2xl object-cover border-2 border-amber-500/50 shadow-xl"
              />
            </div>
          </div>

          {/* Creators Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <Skeleton key={i} className="h-72 rounded-2xl" />
              ))}
            </div>
          ) : creators.length === 0 ? (
            <Card className="border-dashed border-2 border-amber-500/20 bg-amber-500/5">
              <CardContent className="py-16 text-center">
                <Crown className="w-16 h-16 mx-auto mb-4 text-amber-500/30" />
                <h3 className="text-xl font-bold text-foreground mb-2">No Early Creators Yet</h3>
                <p className="text-muted-foreground mb-4">Be among the first 10 creators to earn this exclusive badge automatically!</p>
                <Link to="/marketplace">
                  <Button className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600">
                    <Star className="w-4 h-4 mr-2" />
                    Start Creating
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {creators.map(creator => (
                <CreatorCard key={creator.id} creator={creator} />
              ))}
              
              {/* Remaining spots */}
              {Array.from({ length: 10 - creators.length }).map((_, i) => (
                <Card key={`empty-${i}`} className="border-dashed border-2 border-muted-foreground/20 bg-muted/5">
                  <CardContent className="py-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-muted/20 mx-auto mb-4 flex items-center justify-center">
                      <User className="w-8 h-8 text-muted-foreground/30" />
                    </div>
                    <Badge variant="outline" className="text-muted-foreground">
                      #{creators.length + i + 1} • Available
                    </Badge>
                    <p className="text-sm text-muted-foreground mt-2">Waiting for claimer...</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

        {/* Call to Action */}
          {creators.length < 10 && (
            <div className="mt-12 text-center">
              <Card className="inline-block bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/30">
                <CardContent className="p-6 md:p-8">
                  <Sparkles className="w-10 h-10 text-amber-400 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-foreground mb-2">
                    {10 - creators.length} Spots Remaining!
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Badges are awarded automatically — start creating to earn yours!
                  </p>
                  <Link to="/marketplace">
                    <Button size="lg" className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600">
                      <Crown className="w-5 h-5 mr-2" />
                      Start Creating NFTs
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          )}
        </section>

        {/* Future Badges Notice */}
        <section className="mt-16">
          <Card className="bg-gradient-to-br from-primary/5 to-purple-500/5 border-primary/20">
            <CardContent className="p-6 text-center">
              <Sparkles className="w-8 h-8 text-primary mx-auto mb-3" />
              <h3 className="text-lg font-bold text-foreground mb-2">More Badges Coming Soon!</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                We're constantly adding new exclusive badges and achievements. 
                Stay active to be the first to earn them when they launch!
              </p>
            </CardContent>
          </Card>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function CreatorCard({ creator }: { creator: EarlyCreator }) {
  const displayName = creator.display_name || "Creator";
  const positionBadge = getPositionBadge(creator.position);
  const profileLink = creator.profile_id ? `/profile/${creator.profile_id}` : "#";

  return (
    <Link to={profileLink}>
      <Card className="group relative overflow-hidden bg-gradient-to-br from-card/80 to-card border-amber-500/20 hover:border-amber-500/50 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-amber-500/10">
        {/* Position badge */}
        <div className="absolute top-4 left-4 z-10">
          <Badge className={`bg-gradient-to-r ${positionBadge.color} text-white border-0 px-3 py-1 font-bold shadow-lg`}>
            {positionBadge.label}
          </Badge>
        </div>

        {/* Background glow */}
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-orange-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />

        <CardContent className="relative p-6 pt-16">
          <div className="flex flex-col items-center text-center space-y-4">
            {/* Avatar */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full blur-lg opacity-40 group-hover:opacity-60 transition-opacity" />
              <div className="relative w-24 h-24 rounded-full overflow-hidden border-4 border-amber-500/30 bg-gradient-to-br from-amber-500/20 to-orange-500/20">
                {creator.avatar_url ? (
                  <img 
                    src={creator.avatar_url} 
                    alt={displayName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="w-10 h-10 text-amber-500/50" />
                  </div>
                )}
              </div>
              <div className="absolute -bottom-1 -right-1 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full p-1.5 shadow-lg">
                <Star className="w-4 h-4 text-white" />
              </div>
            </div>

            {/* Name */}
            <div>
              <h3 className="text-lg font-bold text-foreground group-hover:text-amber-400 transition-colors">
                {displayName}
              </h3>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Wallet className="w-4 h-4" />
                {creator.nft_count} NFTs
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDistanceToNow(new Date(creator.claimed_at), { addSuffix: true })}
              </span>
            </div>

            {/* View button */}
            <Button 
              variant="ghost" 
              size="sm" 
              className="opacity-0 group-hover:opacity-100 transition-opacity gap-1 text-amber-400 hover:text-amber-300"
            >
              View Profile
              <ExternalLink className="w-3 h-3" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function getPositionBadge(position: number) {
  switch (position) {
    case 1:
      return { icon: <Trophy className="w-5 h-5" />, color: "from-yellow-400 to-amber-500", label: "🥇 First" };
    case 2:
      return { icon: <Trophy className="w-5 h-5" />, color: "from-gray-300 to-gray-400", label: "🥈 Second" };
    case 3:
      return { icon: <Trophy className="w-5 h-5" />, color: "from-amber-600 to-orange-700", label: "🥉 Third" };
    default:
      return { icon: <Star className="w-5 h-5" />, color: "from-purple-400 to-violet-500", label: `#${position}` };
  }
}
